import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Avatar, 
  IconButton, 
  TextField,
  InputAdornment,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { LocationResult } from './LocationSearch';
import { searchLocations } from '../../data/cities';

interface TripLocationPickerProps {
  onSelectLocation: (location: LocationResult) => void;
  selectedLocation?: LocationResult | null;
}

const TripLocationPicker: React.FC<TripLocationPickerProps> = ({ 
  onSelectLocation,
  selectedLocation
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<LocationResult[]>([]);

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    if (value.trim()) {
      // Search cities from our hardcoded data
      const results = searchLocations(value);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  // Handle location selection
  const handleLocationSelect = (location: LocationResult) => {
    onSelectLocation(location);
    setSearchTerm('');
    setSearchResults([]);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle1" gutterBottom>
        Primary Destination
      </Typography>
      
      <TextField
        fullWidth
        placeholder="Search for a destination"
        value={searchTerm}
        onChange={handleSearchChange}
        variant="outlined"
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      
      {searchResults.length > 0 && (
        <Paper elevation={3} sx={{ mb: 2, maxHeight: 300, overflow: 'auto' }}>
          <Box sx={{ p: 1 }}>
            {searchResults.map((city) => (
              <Box
                key={city.placeId}
                sx={{
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderRadius: 1,
                }}
                onClick={() => handleLocationSelect(city)}
              >
                <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="body1">{city.mainText}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {city.secondaryText}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
      
      {selectedLocation && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Destination
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <LocationOnIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  {selectedLocation.mainText}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedLocation.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedLocation.coordinates.lat.toFixed(6)}, {selectedLocation.coordinates.lng.toFixed(6)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
      
      {!selectedLocation && !searchResults.length && (
        <Grid container spacing={1} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, ml: 1 }}>
            Start typing a city name
          </Typography>
          {/* Display a few city avatars as suggestions */}
          {searchLocations('').slice(0, 10).map((city) => (
            <Grid item key={city.placeId}>
              <IconButton 
                sx={{ 
                  width: 40, 
                  height: 40,
                  bgcolor: 'action.selected',
                  '&:hover': { bgcolor: 'primary.light' }
                }}
                onClick={() => handleLocationSelect(city)}
              >
                <Typography variant="caption">{city.mainText.charAt(0)}</Typography>
              </IconButton>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default TripLocationPicker; 